import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNotificationDispatches } from "services/hooks/rentals";
import { CHANNEL_OPTIONS, DISPATCH_STATUS_OPTIONS } from "../constants";

const LOG_TYPES = [
  { label: "Tenant Dispatches", value: "TENANT" },
  { label: "Payout Dispatches", value: "PAYOUT" },
];

const DispatchLogsTab = () => {
  const [message, setMessage] = useState({ type: "", text: "" });
  const [logType, setLogType] = useState("TENANT");
  const [statusFilter, setStatusFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const {
    dispatches,
    payoutDispatches,
    isLoadingDispatches,
    dispatchesError,
    fetchDispatches,
    fetchPayoutDispatches,
  } = useNotificationDispatches({ status: statusFilter, channel: channelFilter });

  const logs = logType === "TENANT" ? dispatches : payoutDispatches;

  const loadLogs = async () => {
    if (logType === "TENANT") {
      await fetchDispatches();
    } else {
      await fetchPayoutDispatches();
    }
  };

  useEffect(() => {
    loadLogs();
  }, [logType]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Dispatch Logs</Typography>
        <Button variant="outlined" onClick={loadLogs}>
          Refresh
        </Button>
      </Box>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {dispatchesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {dispatchesError}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Log Type</InputLabel>
            <Select label="Log Type" value={logType} onChange={(event) => setLogType(event.target.value)}>
              {LOG_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <MenuItem value="">All</MenuItem>
              {DISPATCH_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Channel</InputLabel>
            <Select label="Channel" value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
              <MenuItem value="">All</MenuItem>
              {CHANNEL_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={loadLogs}>
            Apply
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        {isLoadingDispatches ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No dispatch logs found.</TableCell>
                </TableRow>
              ) : (
                logs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.status || "—"}</TableCell>
                    <TableCell>{row.channel || "—"}</TableCell>
                    <TableCell>{row.related_rule?.name || row.related_rule?.id || row.related_payout_rule?.name || row.related_payout_rule?.id || "—"}</TableCell>
                    <TableCell>{row.related_template?.name || row.related_template?.id || "—"}</TableCell>
                    <TableCell>{row.recipient || row.recipient_email || row.recipient_phone || "—"}</TableCell>
                    <TableCell>{row.date_created || "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default DispatchLogsTab;
